import { useState, useEffect } from 'react';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { useTheme } from '../theme';
import { trackCtaClick } from '../../lib/analytics';
import { Picture } from './Picture';

const LOGO_LIGHT = 'logo/baran-light';
const LOGO_DARK  = 'logo/baran-dark';
const LOGO_SIZES = '(min-width: 1024px) 300px, (min-width: 640px) 220px, 140px';

const navLinks = [
  { label: 'Ana Sayfa', href: '#hero' },
  { label: 'Hakkında', href: '#about' },
  { label: 'Galeri', href: '#gallery' },
  { label: 'Donatılar', href: '#amenities' },
  { label: 'Kat Planları', href: '#floorplans' },
  { label: 'Konum', href: '#location' },
  { label: 'SSS', href: '#faq' },
  { label: 'İletişim', href: '#contact' },
];

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const logoClass = 'h-auto w-[140px] sm:w-[220px] lg:w-[300px] max-w-none object-contain transition-all duration-300';

  const linkClass = `text-sm tracking-wide transition-opacity hover:opacity-100 ${
    isScrolled ? 'text-gray-700 dark:text-gray-200 opacity-75' : 'text-white/75'
  }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/90 dark:bg-slate-900/90 backdrop-blur-md shadow-md'
          : 'bg-transparent'
      }`}
    >
      <div className="mx-0 xl:container xl:mx-auto px-0 xl:px-8">
        <div className="flex items-center h-12 sm:h-16 xl:h-20 gap-2 xl:gap-8">

          {/* Logo */}
          <a href="#hero" className="flex-shrink-0">
            {!isScrolled ? (
              <Picture media={LOGO_DARK} alt="Baran Group" sizes={LOGO_SIZES} width={300}
                loading="eager" className={`${logoClass} brightness-0 invert`} />
            ) : (
              <>
                <Picture media={LOGO_LIGHT} alt="Baran Group" sizes={LOGO_SIZES} width={300}
                  loading="eager" className={`${logoClass} dark:hidden`} />
                <Picture media={LOGO_DARK} alt="Baran Group" sizes={LOGO_SIZES} width={300}
                  loading="eager" className={`${logoClass} hidden dark:block brightness-0 invert`} />
              </>
            )}
          </a>

          {/* Desktop nav links — center */}
          <div className="hidden xl:flex flex-1 items-center justify-center gap-6">
            {navLinks.map((link) => (
              <a key={link.href} href={link.href} className={linkClass}>
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop actions — right */}
          <div className="hidden xl:flex items-center gap-3 flex-shrink-0">
            <button
              onClick={toggleTheme}
              aria-label="Temayı değiştir"
              className={`p-2 rounded-lg transition-all duration-300 ${
                isScrolled
                  ? 'bg-slate-100 text-slate-900 hover:bg-slate-200 dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700'
                  : 'bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm'
              }`}
            >
              <>
                <Sun size={16} className="hidden dark:block" />
                <Moon size={16} className="block dark:hidden" />
              </>
            </button>
            <a
              href="#contact"
              onClick={() => trackCtaClick('Bize Ulaşın', 'navbar_desktop')}
              className="px-5 py-2.5 bg-slate-900 text-white dark:bg-white dark:text-slate-900 rounded-lg hover:bg-slate-700 dark:hover:bg-slate-100 transition-all duration-300 text-sm tracking-wide"
            >
              Bize Ulaşın
            </a>
          </div>

          {/* Mobile / Tablet actions */}
          <div className="flex items-center gap-2 ml-auto xl:hidden">
            {/* "Bize Ulaşın" — iPad Pro ve üstünde hamburger yanında görünür */}
            <a
              href="#contact"
              onClick={() => trackCtaClick('Bize Ulaşın', 'navbar_tablet')}
              className={`hidden sm:inline-flex items-center px-4 py-2 rounded-lg text-sm tracking-wide transition-all duration-300 ${
                isScrolled
                  ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900 hover:bg-slate-700 dark:hover:bg-slate-100'
                  : 'bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm border border-white/30'
              }`}
            >
              Bize Ulaşın
            </a>
            <button
              onClick={toggleTheme}
              aria-label="Temayı değiştir"
              className={`p-2 ${isScrolled ? 'text-gray-800 dark:text-gray-100' : 'text-white'}`}
            >
              <>
                <Sun size={22} className="hidden dark:block" />
                <Moon size={22} className="block dark:hidden" />
              </>
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 ${isScrolled ? 'text-gray-800 dark:text-gray-100' : 'text-white'}`}
              aria-label="Menü"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="xl:hidden bg-white/95 dark:bg-slate-900/95 backdrop-blur-md rounded-lg mt-2 p-4 shadow-lg">
            <div className="flex flex-col space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-gray-800 dark:text-gray-100 hover:text-slate-900 dark:hover:text-white transition-colors py-2"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={() => { trackCtaClick('Bize Ulaşın', 'navbar_mobile'); setIsMobileMenuOpen(false); }}
                className="px-6 py-3 bg-slate-900 text-white dark:bg-white dark:text-slate-900 rounded-lg text-center hover:bg-slate-800 dark:hover:bg-slate-100 transition-all"
              >
                Bize Ulaşın
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
